// React Query mutation hooks for PRO Item Design. Each mutation invalidates
// the shared tree cache on success so the left panel reflects the change.
//
// Slice 5 covers the domain CRUD + reorder + the cross-cutting createSubArea
// trigger (called from the tree's hover-action button on a domain row).
// Subsequent slices add updateSubArea / deleteSubArea / item mutations.

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { proItemDesignApi } from '../api/pro-item-design.adapter';
import { proItemDesignQueryKeys } from './use-pro-tree';
import type {
  CreateDomainInput,
  CreateItemInput,
  CreateSubAreaInput,
  ProDomain,
  ProItem,
  ProItemHeader,
  ProItemDesignApiError,
  ProSubArea,
  ReorderInput,
  SaveItemInput,
  UpdateDomainInput,
  UpdateSubAreaInput,
} from '../types/pro-item-design.types';

type ApiError = ProItemDesignApiError | Error;

export function useCreateDomain() {
  const qc = useQueryClient();
  return useMutation<ProDomain, ApiError, CreateDomainInput>({
    mutationFn: (input) => proItemDesignApi.createDomain(input),
    onSuccess: (created) => {
      // SHCT-388 — seed the tree so a just-created node exists in cache the
      // instant the caller selects it (the Save-time create replaces a draft
      // whose temp id is about to be dropped); the invalidate then reconciles.
      qc.setQueryData<ProDomain[]>(proItemDesignQueryKeys.tree(), (old) =>
        old ? [...old, { ...created, subAreas: created.subAreas ?? [] }] : old,
      );
      qc.invalidateQueries({ queryKey: proItemDesignQueryKeys.tree() });
    },
  });
}

export function useUpdateDomain() {
  const qc = useQueryClient();
  return useMutation<ProDomain, ApiError, { id: number; input: UpdateDomainInput }>({
    mutationFn: ({ id, input }) => proItemDesignApi.updateDomain(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: proItemDesignQueryKeys.tree() });
    },
  });
}

export function useDeleteDomain() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (id) => proItemDesignApi.deleteDomain(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: proItemDesignQueryKeys.tree() });
    },
  });
}

export function useCreateSubArea() {
  const qc = useQueryClient();
  return useMutation<
    ProSubArea,
    ApiError,
    { domainId: number; input: CreateSubAreaInput }
  >({
    mutationFn: ({ domainId, input }) => proItemDesignApi.createSubArea(domainId, input),
    onSuccess: (created) => {
      // SHCT-388 — seed under the parent domain (see useCreateDomain).
      qc.setQueryData<ProDomain[]>(proItemDesignQueryKeys.tree(), (old) =>
        old?.map((d) =>
          d.id === created.domainId
            ? { ...d, subAreas: [...d.subAreas, { ...created, items: created.items ?? [] }] }
            : d,
        ) ?? old,
      );
      qc.invalidateQueries({ queryKey: proItemDesignQueryKeys.tree() });
    },
  });
}

export function useUpdateSubArea() {
  const qc = useQueryClient();
  return useMutation<ProSubArea, ApiError, { id: number; input: UpdateSubAreaInput }>({
    mutationFn: ({ id, input }) => proItemDesignApi.updateSubArea(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: proItemDesignQueryKeys.tree() });
    },
  });
}

export function useDeleteSubArea() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (id) => proItemDesignApi.deleteSubArea(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: proItemDesignQueryKeys.tree() });
    },
  });
}

export function useCreateItem() {
  const qc = useQueryClient();
  return useMutation<ProItemHeader, ApiError, { subAreaId: number; input: CreateItemInput }>({
    mutationFn: ({ subAreaId, input }) => proItemDesignApi.createItem(subAreaId, input),
    onSuccess: (created) => {
      // SHCT-388 — seed the header under its sub-area so it exists in cache
      // before the full detail save/publish round-trip completes.
      qc.setQueryData<ProDomain[]>(proItemDesignQueryKeys.tree(), (old) =>
        old?.map((d) =>
          d.id === created.domainId
            ? {
                ...d,
                subAreas: d.subAreas.map((s) =>
                  s.id === created.subAreaId ? { ...s, items: [...s.items, created] } : s,
                ),
              }
            : d,
        ) ?? old,
      );
      qc.invalidateQueries({ queryKey: proItemDesignQueryKeys.tree() });
      // Do NOT seed the item-detail cache here: the create endpoint returns only
      // a header node (no linkedQuestions / scope / schedule), so seeding it would
      // make ItemDetail render a partial object and crash on `linkedQuestions`.
      // Invalidate instead so the detail loads the full item via getItem.
      qc.invalidateQueries({ queryKey: proItemDesignQueryKeys.item(created.id) });
    },
  });
}

export function useDeleteItem() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (id) => proItemDesignApi.deleteItem(id),
    onSuccess: (_void, id) => {
      qc.invalidateQueries({ queryKey: proItemDesignQueryKeys.tree() });
      qc.removeQueries({ queryKey: proItemDesignQueryKeys.item(id) });
    },
  });
}

// PRD §3.4 — Save Draft (status stays DRAFT). BE skips required-field
// validation; the FE form still clamps ranges client-side. The item cache is
// updated in-place so the panel sees the canonical record immediately.
export function useSaveDraftItem() {
  const qc = useQueryClient();
  return useMutation<ProItem, ApiError, { id: number; input: SaveItemInput }>({
    mutationFn: ({ id, input }) => proItemDesignApi.saveDraftItem(id, input),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: proItemDesignQueryKeys.tree() });
      qc.setQueryData(proItemDesignQueryKeys.item(updated.id), updated);
    },
  });
}

// PRD §3.4 — Save (publish; status flips to PUBLISHED). BE enforces the full
// required-field set; the FE pre-validates so the user sees inline errors
// before paying the round trip (Slice 13).
export function usePublishItem() {
  const qc = useQueryClient();
  return useMutation<ProItem, ApiError, { id: number; input: SaveItemInput }>({
    mutationFn: ({ id, input }) => proItemDesignApi.publishItem(id, input),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: proItemDesignQueryKeys.tree() });
      qc.setQueryData(proItemDesignQueryKeys.item(updated.id), updated);
    },
  });
}

// PRD §3.1 — sibling reorder (up/down arrows). FE builds the post-move
// orderedIds list and sends in one shot; BE re-numbers atomically.
export function useReorder() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, ReorderInput>({
    mutationFn: (input) => proItemDesignApi.reorder(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: proItemDesignQueryKeys.tree() });
    },
  });
}
